import React, { useMemo, useState } from 'react';

import useCreateTag from '@hooks/API/UseCreateTag';
import useTagCatalog from '@hooks/CachedLists/UseTagCatalog';

import TagPicker from '@components/TagPicker/TagPicker';
import TagsStrip from '@components/TagPicker/TagsStrip';

/**
 * Selezione dei tag applicati a un oggetto (Model, Customer, Playlist, Document, ...).
 * Legge il catalogo condiviso e delega la persistenza a onAddTag/onRemoveTag.
 *
 * tags        tag gia' applicati all'oggetto, [{ ID, Name }]
 * allowCreate se falso nasconde la riga di creazione al volo
 * publicOnly  se vero esclude i tag privati dal catalogo (es. News, che non ha un concetto
 *              di tag privato: mostrarli confonderebbe un tag "solo mio" su contenuto pubblico)
 * trigger     opzionale: sostituisce il trigger di default con i nomi dei tag
 * stripMaxWidth larghezza massima della striscia: piu' stretta negli header dei drawer
 * stripShowAll  mostra tutti i tag andando a capo, senza la pill "+N": per i contesti
 *               con spazio verticale, come la sezione Tags del progetto
 */
const ObjectTagPicker = ({
    itemType,
    tags = [],
    onAddTag,
    onRemoveTag,
    allowCreate = true,
    publicOnly = false,
    outerLoading,
    stripMaxWidth,
    stripShowAll,
    trigger,
    ...pickerProps
}) => {
    const catalog = useTagCatalog(itemType, { publicOnly });
    const createTag = useCreateTag(itemType);
    const [isSaving, setIsSaving] = useState(false);

    const options = useMemo(
        () =>
            catalog.map((tag) => ({
                key: tag.ID,
                name: tag.Name,
                usageCount: tag.UsageCount,
                isPrivate: tag.isPrivate,
                tag,
            })),
        [catalog]
    );

    // il nome applicato all'oggetto puo' avere maiuscole diverse da quello del catalogo:
    // l'abbinamento e' sempre sull'ID, quindi qui si mostra il nome cosi' come arriva
    const selected = useMemo(() => tags.map((tag) => ({ key: tag.ID, name: tag.Name, tag })), [tags]);

    const runWithLoading = async (action) => {
        setIsSaving(true);
        await action();
        setIsSaving(false);
    };

    const handleToggle = (option) => {
        const isApplied = tags.some((tag) => tag.ID === option.key);
        runWithLoading(() => (isApplied ? onRemoveTag(option.tag) : onAddTag(option.tag)));
    };

    // createTag e' null senza la policy CreatePublicTags: in quel caso la riga di creazione
    // non compare proprio. Il tag appena creato viene subito applicato all'oggetto.
    const handleCreate =
        allowCreate && createTag
            ? (name) =>
                  runWithLoading(async () => {
                      const createdTag = await createTag(name);
                      if (createdTag) {
                          await onAddTag(createdTag);
                      }
                  })
            : undefined;

    const triggerLabel = tags.length ? `Tags: ${tags.map((tag) => tag.Name).join(', ')}` : 'Add tags';

    const defaultTrigger = <TagsStrip tags={tags} maxWidth={stripMaxWidth} showAll={stripShowAll} />;

    return (
        <TagPicker
            trigger={trigger || defaultTrigger}
            triggerLabel={triggerLabel}
            options={options}
            selected={selected}
            onToggle={handleToggle}
            onCreate={handleCreate}
            selectedLabel="applied"
            emptyMessage="No tags defined for this object type"
            loading={isSaving || outerLoading}
            {...pickerProps}
        />
    );
};

export default ObjectTagPicker;
